import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { Tradable } from "@models/interfaces/tradable";
import { AssetsTradeScrollService } from "@main-services/assets-trade-scroll.service";
import { Observable } from "rxjs";
import { PaginatedEvent } from "@models/pagination_event";
import {
	API_ETC_TYPE,
	API_ETF_TYPE,
	API_METAL_TYPE,
	API_SECURITY_TYPE,
	API_STOCK_TYPE,
	PRICES_SCROLL_PER_PAGE
} from "@helpers/constants";
import { Timespan } from "@helpers/const/timespan";

@Injectable()
export abstract class AssetListService {
	constructor(public assetTradeScrollService: AssetsTradeScrollService) {}

	public initList(activeFlow: string, filters: string[]): void {
		this.assetTradeScrollService.setActiveFlow(activeFlow);
		this.assetTradeScrollService.initList(PRICES_SCROLL_PER_PAGE, filters);
	}

	public getList$(isPriceOrder: boolean = false): Observable<Tradable[]> {
		return this.assetTradeScrollService.getListObservable().pipe(
			map((tradables: Tradable[]) => {
				const activeTradables = tradables.filter((t) => t.isBuyActive());

				//filtered assets for UK users
				const filteredAssetsForUKUsers = activeTradables.filter(
					(t) =>
						t.getAssetGroupName() !== API_STOCK_TYPE &&
						t.getAssetGroupName() !== API_ETF_TYPE &&
						t.getAssetGroupName() !== API_ETC_TYPE &&
						t.getAssetGroupName() !== API_SECURITY_TYPE &&
						t.getAssetGroupName() !== API_METAL_TYPE
				);

				return this.filterList(filteredAssetsForUKUsers, isPriceOrder);
			})
		);
	}

	public updateList(
		sortColumn: string,
		sortDir: string,
		timeframe: Timespan,
		filters: string[],
		search: string
	): void {
		this.assetTradeScrollService.clearEndOfList();
		const event = new PaginatedEvent();

		if (sortColumn && sortDir) {
			if (sortColumn === "change") {
				// Api syntax adjustment:
				switch (timeframe) {
					case Timespan.Week:
						sortColumn = "price_change_1w";
						break;
					case Timespan.Month:
						sortColumn = "price_change_1m";
						break;
					case Timespan.Year:
						sortColumn = "price_change_1y";
						break;
					default:
						sortColumn = "price_change_24h";
						break;
				}
			}

			event.setSortBy(sortColumn.toLowerCase());
			event.setSortDirection(sortDir);
		}

		if (filters && filters.length > 0) {
			event.setType(filters.map((x) => x.toLowerCase()));
		}

		if (search) {
			event.setSearchQuery(search);
		}

		event.setPageSize(PRICES_SCROLL_PER_PAGE);
		this.assetTradeScrollService.fetchApiData(event);
	}

	public refetchList(filters: string[]): void {
		this.assetTradeScrollService.refetchList(filters);
	}

	public fetchNextPage(): void {
		this.assetTradeScrollService.fetchListUpdate();
	}

	public getLoadingObservable$(): Observable<boolean> {
		return this.assetTradeScrollService.getLoadingObservable();
	}

	public getErrorObservable$(): Observable<boolean> {
		return this.assetTradeScrollService.getFetchErrorObservable();
	}

	public abstract filterList(list: Tradable[], isPriceOrder?: boolean): Tradable[];
}
